import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Bot, BotIcon, ChevronDown, Send, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import type {
  ChatbotMemberSpecificPayloadType,
  ChatbotMessageType,
} from "@/types";
import {
  getComponentData,
  getCurrentTimestamp,
  getUniqueId,
} from "@/lib/chatbot-utils";
import ChatbotMessage from "./chatbot-message";
import {
  useChatbotDefaultQuestionsQuery,
  useChatbotQuery,
  useMemberSpecificQuery,
} from "@/hooks/use-chatbot";
import useUserInfo from "@/hooks/use-user-info";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "@/store/store";
import { setIsOpen, setUserDefaultQuery } from "@/store/chatbot/chatbot-slice";
import { CHATBOT_COMPONENT_NAME, CHATBOT_DEFAULT_REPLY } from "@/constants";

type ChatbotPopupProps = Record<string, never>;

const regex = /^{{.*}}$/;

const ChatbotPopup: React.FC<ChatbotPopupProps> = () => {
  const userInfo = useUserInfo();
  const dispatch = useDispatch();
  useChatbotDefaultQuestionsQuery();

  const memberSpecificQueryPayload = {
    appVersion: "",
    divisionId: userInfo.divisionId,
    groupId: userInfo.groupId,
    loginGroupId: userInfo.loginGroupId,
    memberId: userInfo.memberId,
    planId: userInfo.planId,
    username: userInfo.username,
    token: userInfo.accessToken,
  };

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const typingInterruptRef = useRef(false);

  const { isOpen, userDefaultQuery } = useSelector(
    (state: RootState) => state.chatbot,
  );

  const [userInput, setUserInput] = useState<string>("");
  const [isTyping, setIsTyping] = useState(false);
  const [showScrollDown, setShowScrollDown] = useState(false);
  const [messages, setMessages] = useState<ChatbotMessageType[]>([
    {
      id: "1",
      isUser: false,
      textMessage:
        "Welcome to the Virtual Assistant! Ask me a question and I will try to answer it. This is only a description of benefits and not a guarantee of payment. Benefits are subject to the plans and provisions and limitations in effect at the time services were rendered.",
      containsComponentToRender: false,
      relatedQuestions: [],
      timestamp: getCurrentTimestamp(new Date()),
    },
  ]);

  useEffect(() => {
    return () => {
      dispatch(setIsOpen(false));
    };
  }, [dispatch]);

  useEffect(() => {
    if (isOpen) {
      if (userDefaultQuery.length > 0) {
        postUserQuery(userDefaultQuery[0]);
      }
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, userDefaultQuery]);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [messages]);

  const sendMemberSpecificQuery = useMemberSpecificQuery({
    onSuccess: (response) => {
      setMessages([...messages, response]);
    },
    onError: (_error) => {
      const memberSpecificErrReply: ChatbotMessageType = {
        id: getUniqueId(),
        isUser: false,
        containsComponentToRender: true,
        timestamp: getCurrentTimestamp(new Date()),
        textMessage: CHATBOT_DEFAULT_REPLY,
        component: {
          componentName: CHATBOT_COMPONENT_NAME.PROVIDER_DETAILS,
        },
      };
      setMessages([...messages, memberSpecificErrReply]);
    },
  });

  const sendMessage = useChatbotQuery({
    onSuccess: (response) => {
      if (regex.test(response.textMessage)) {
        const qnaComponent = getComponentData(response.textMessage);
        const requiredMemberSpecificQueryPayload: ChatbotMemberSpecificPayloadType =
          {
            ...memberSpecificQueryPayload,
            widgetId: qnaComponent!.widgetId,
            componentName: qnaComponent!.componentName,
            serviceId: qnaComponent!.serviceId,
            networkType: qnaComponent!.networkType,
            relatedQuestions:
              response?.relatedQuestions && response.relatedQuestions.length > 0
                ? response?.relatedQuestions
                : [],
          };
        sendMemberSpecificQuery.mutate(requiredMemberSpecificQueryPayload);
      } else {
        setMessages([...messages, response]);
      }
    },
    onError: (_error) => {
      const chatbotErrReply: ChatbotMessageType = {
        id: getUniqueId(),
        isUser: false,
        containsComponentToRender: true,
        timestamp: getCurrentTimestamp(new Date()),
        textMessage: CHATBOT_DEFAULT_REPLY,
        component: {
          componentName: CHATBOT_COMPONENT_NAME.PROVIDER_DETAILS,
        },
      };
      setMessages([...messages, chatbotErrReply]);
    },
  });

  const postUserQuery = (query?: string) => {
    if (
      !sendMessage.isPending &&
      !sendMemberSpecificQuery.isPending &&
      (query ? query.length > 0 : userInput.length > 0)
    ) {
      if (isTyping) typingInterruptRef.current = true;
      setMessages([
        ...messages,
        {
          id: getUniqueId(),
          isUser: true,
          textMessage: query ? query : userInput.trim(),
          timestamp: getCurrentTimestamp(new Date()),
        },
      ]);
      sendMessage.mutate({ question: query ? query : userInput.trim() });
      setUserInput("");
    }
  };

  const onUserInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUserInput(event.target.value);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      postUserQuery();
    }
  };

  const handleScroll = () => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 50;
    if (!atBottom) {
      setShowScrollDown(true);
    } else if (atBottom) {
      setShowScrollDown(false);
    }
  };

  const toggleOpen = () => {
    if (isOpen) {
      setMessages([
        {
          id: "1",
          isUser: false,
          textMessage:
            "Welcome to the Virtual Assistant! Ask me a question and I will try to answer it. This is only a description of benefits and not a guarantee of payment. Benefits are subject to the plans and provisions and limitations in effect at the time services were rendered.",
          containsComponentToRender: false,
          relatedQuestions: [],
          timestamp: getCurrentTimestamp(new Date()),
        },
      ]);
      setShowScrollDown(false);
      dispatch(setUserDefaultQuery([]));
      setUserInput("");
      dispatch(setIsOpen(false));
    } else {
      dispatch(setIsOpen(true));
    }
  };

  const scrollToBottom = () => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  return (
    <>
      {/* {isOpen && (
                <div
                    // add bg-black/5 backdrop-blur-sm for blur effect now its removed for transparent background
                    className={`fixed z-50 inset-0 transition-opacity duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] ${isOpen ? "opacity-100 visible" : "opacity-0 invisible"}`}
                    style={{
                        willChange: 'opacity',
                    }}
                    onClick={toggleOpen}
                />
            )} */}
      {/* Floating Button */}
      <Button
        onClick={toggleOpen}
        className={`focus:ring-primary/30 hide-from-third-app fixed bottom-6 right-6 z-50 size-14 rounded-full bg-primary text-primary-foreground shadow-lg ring-2 ring-primary-light ring-offset-2 ring-offset-background transition-transform duration-300 ease-out hover:scale-110 hover:shadow-2xl focus:outline-none focus:ring-4 active:scale-95 ${!isOpen ? "animate-float" : ""}`}
        style={{
          transformOrigin: "center",
          backfaceVisibility: "hidden",
          willChange: "transform, box-shadow",
        }}
        aria-label={isOpen ? "Close Chat" : "Open Chat"}
      >
        <div
          className={`transition-transform duration-500 ease-out ${
            isOpen ? "rotate-90" : "rotate-0"
          }`}
        >
          {isOpen ? (
            <X
              className="text-primary-foreground"
              style={{ width: "24px", height: "24px" }}
            />
          ) : (
            <Bot
              className="text-primary-foreground"
              style={{ width: "28px", height: "28px" }}
            />
          )}
        </div>
      </Button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 50, x: 0 }}
            animate={{
              opacity: 1,
              y: 0,
              transition: {
                type: "spring",
                stiffness: 260,
                damping: 15,
              },
            }}
            exit={{
              opacity: 0,
              y: 50,
              transition: {
                duration: 0.2,
                ease: "easeInOut",
              },
            }}
            className="fixed bottom-20 right-6 z-50 flex h-[500px] w-[400px] flex-col overflow-hidden rounded-xl bg-white shadow-xl"
          >
            {/* Header */}
            <div className="flex items-center justify-between bg-primary px-4 py-3 text-white">
              <div className="flex items-center gap-3">
                <div className="flex size-9 items-center justify-center rounded-full bg-white/20">
                  <BotIcon className="size-5" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold">Virtual Assistant</h3>
                  <p className="text-xs opacity-80">Online now</p>
                </div>
              </div>
            </div>

            {/* Messages section */}
            <div
              ref={containerRef}
              onScroll={handleScroll}
              className="flex-1 space-y-4 overflow-y-auto p-4"
              role="log"
              aria-live="polite"
            >
              {messages.map((msg) => (
                <ChatbotMessage
                  key={msg.id}
                  message={msg}
                  recentMessageId={messages[messages.length - 1].id}
                  postUserInput={postUserQuery}
                  onTyping={(t) => setIsTyping(t)}
                  typingInterruptRef={typingInterruptRef}
                />
              ))}
            </div>

            {sendMessage.isPending || sendMemberSpecificQuery.isPending ? (
              <div className="mb-2 flex items-center gap-1 pl-2 text-sm text-gray-800">
                <div className="space-x-0.6 flex h-4 items-center">
                  <span
                    className="typing-dot"
                    style={{ animationDelay: "0s" }}
                  />
                  <span
                    className="typing-dot"
                    style={{ animationDelay: "0.1s" }}
                  />
                  <span
                    className="typing-dot"
                    style={{ animationDelay: "0.2s" }}
                  />
                  <span
                    className="typing-dot"
                    style={{ animationDelay: "0.3s" }}
                  />
                </div>
              </div>
            ) : null}

            {/* Input */}
            <div className="border-t p-4">
              <div className="flex min-w-0 gap-2">
                {/*
                  3.3.2 Labels or Instructions — A: the field had only a placeholder, so a
                  screen reader announced an unlabelled edit box.
                  2.4.7 Focus Visible — AA: `focus-visible:outline-none` removed the focus
                  indicator with nothing put in its place, leaving keyboard users unable to
                  tell the field was focused.
                */}
                <input
                  ref={inputRef}
                  aria-label="Type your message"
                  className="flex h-9 w-full min-w-0 overflow-hidden truncate whitespace-nowrap rounded-md bg-transparent px-1 text-medium transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                  placeholder="Type your message..."
                  value={userInput}
                  onChange={onUserInputChange}
                  onKeyDown={onKeyDown}
                  maxLength={500}
                />
                {/* 4.1.2 Name, Role, Value — A: icon-only, so it was announced as just
                    "button" with no indication that it sends the message. */}
                <Button
                  className="flex size-10 items-center justify-center rounded-full bg-primary text-white transition-colors hover:bg-primary"
                  type="button"
                  aria-label="Send message"
                  onClick={() => postUserQuery()}
                >
                  <Send aria-hidden="true" className="size-5" />
                </Button>
              </div>
            </div>

            {showScrollDown && (
              <button
                className="absolute bottom-20 right-4 animate-bounce rounded-full bg-gray-200 p-2 shadow"
                onClick={scrollToBottom}
                aria-label="Scroll to bottom"
              >
                <ChevronDown className="size-5" />
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatbotPopup;
